// MARK://dhk8/pos/pages/ReceiptPage
// Ticket detail (/tickets/:id, also /receipt and /receipt/:orderId for back-compat):
//   • CLOSED ticket → the customer receipt as a rounded "Apple-receipt" card (brand
//     glyph, monospace amounts, hairline rows) plus a CFDI-lite section (factura 4.0 ·
//     UUID · TIMBRADA · Ver XML / Ver PDF, or a "Facturar" placeholder when not invoiced).
//     Toolbar: Reimprimir · Reenviar (correo / WhatsApp via backend) · Volver.
//   • OPEN order → the same card layout but labelled "PRE-CUENTA · no fiscal": no folio
//     fiscal, no UUID, no CFDI section, with a note that it's NOT a payment receipt.
//     Toolbar: Imprimir pre-cuenta · Reenviar · Volver.
//
// Data: lib/clients/receipts.ts getTicket() — under VITE_API_STUB=1 it serves
// SEED_RECEIPTS (closed) / SEED_ORDERS (open). In live builds it returns null today,
// so we fall back to the active order in the order-store (set by PaymentDialog right
// before redirecting here). The "Reenviar" action calls resendReceipt() — the client
// never composes or sends the message itself (see clients/receipts.ts header).

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Printer, Send, ArrowLeft, FileText, FileCheck2, FileClock, RotateCcw, Unlock, DollarSign, Pencil, Ban } from 'lucide-react'
import QRCode from 'qrcode'

import { getTicket, resendReceipt, type ReceiptCfdi, type ReceiptDto } from '../lib/clients/receipts.js'
import { listOrderPayments, refundOrderPayment, reopenOrder, cancelOrder, type BackendOrderPaymentDto } from '../lib/clients/orders.js'
import { getUserById } from '../lib/clients/users.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useOrderStore, type OrderDto, type RegisteredPayment } from '../stores/order-store.js'
import RefundDialog from '../components/RefundDialog.js'
import TicketPreview from '../features/TicketPreview.js'
import InvoiceAttemptsTimeline from '../features/billing/InvoiceAttemptsTimeline.js'
import { useEnqueueReceipt, useEnqueuePrecuenta } from '../hooks/usePrinting.js'
import FacturarSheet from '../features/billing/FacturarSheet.js'
import { labelForUso } from '../features/billing/sat-catalogs.js'
import { formatCents } from '../lib/money.js'
import { getBranding } from '../lib/branding-loader.js'

// Subdominio fijo del POS white-label (no es por-tenant: el QR siempre lleva al
// mismo servicio dhk8-public-tickets). Si en el futuro se white-labela el host
// del ticket público, esto pasa a una variable de runtime (env / branding).
const PUBLIC_TICKETS_HOST = 'tickets.nintaigo.com'
const POS_PUBLIC_HOST = 'pos.nintaigo.com'

/**
 * UUID-shaped guard. We don't want the QR resolving "Atendió: 555bfa2a-..." UUID
 * strings as if they were display names — when waiterName looks like a UUID, the
 * caller is expected to resolve it via getUserById and fall back to "Cajero".
 */
function looksLikeUuid(s: string | null | undefined): boolean {
  return !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
}

const HUMAN_METHOD: Record<string, string> = {
  CASH: 'Efectivo', CARD_DEBIT: 'Tarjeta débito', CARD_CREDIT: 'Tarjeta crédito',
  TRANSFER: 'Transferencia', MEAL_VOUCHER: 'Vale',
}

function formatDateMx(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d)
}

export default function ReceiptPage() {
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId?: string }>()

  const storeOrder = useOrderStore((s) => s.order)
  const storePayments = useOrderStore((s) => s.payments)
  const storeTable = useOrderStore((s) => s.table)

  const ticketQuery = useQuery({
    queryKey: ['ticket', orderId ?? 'latest'],
    queryFn: ({ signal }) => getTicket(orderId, signal),
    staleTime: 30_000,
  })

  // Fallback for live builds (no tickets backend yet): the order the cashier just closed
  // (or, if it isn't closed, the order currently open in the store rendered as a pre-cuenta).
  const fallback: ReceiptDto | null = useMemo(() => {
    if (!storeOrder) return null
    const order: OrderDto = storeOrder
    const payments: RegisteredPayment[] = storePayments
    const paidSum = payments.reduce((s, p) => s + p.amountCents, 0)
    const closed = order.status === 'CLOSED'
    return {
      id: order.id,
      order,
      payments: closed ? payments : [],
      tipCents: order.tipCents,
      cashGivenCents: null,
      changeCents: closed ? Math.max(0, paidSum - order.totalCents) : 0,
      waiterName: storeTable.waiterName,
      tableCode: storeTable.tableCode,
      closedAt: order.closedAt ?? order.openedAt ?? new Date().toISOString(),
      cfdi: null,
      kind: closed ? 'receipt' : 'precuenta',
    }
  }, [storeOrder, storePayments, storeTable])

  const receipt = ticketQuery.data ?? fallback
  const isPrecuenta = receipt?.kind === 'precuenta'

  // ── "Reenviar" prompt (email / WhatsApp — sent server-side) ──
  const [resendOpen, setResendOpen] = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [resendWa, setResendWa] = useState('')
  const [resendBusy, setResendBusy] = useState(false)
  const [resendErr, setResendErr] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 3000) }
  function openResend() { setResendEmail(''); setResendWa(''); setResendErr(null); setResendOpen(true) }
  async function submitResend() {
    if (!receipt) return
    setResendBusy(true); setResendErr(null)
    try {
      const r = await resendReceipt(receipt.id, { email: resendEmail.trim() || undefined, whatsapp: resendWa.trim() || undefined })
      setResendOpen(false)
      showToast(`${isPrecuenta ? 'Pre-cuenta enviada' : 'Ticket enviado'} a ${r.sentTo}`)
    } catch (e) {
      setResendErr(e instanceof Error ? e.message : 'No se pudo enviar')
    } finally { setResendBusy(false) }
  }

  // ── "Reembolsar" — refund a payment on a PAGADO ticket (PIN-gated) ──
  // Cuando hay 2+ pagos reembolsables (orden con tenders mixtos / split), el cajero
  // elige CUÁL pago reembolsar; con uno solo se preselecciona. Cargamos los pagos
  // reales (listOrderPayments) al abrir el diálogo en lugar de asumir el primero.
  const currentUserId = useAuthStore((s) => s.user?.id)
  const [refundOpen, setRefundOpen] = useState(false)
  const [refundReason, setRefundReason] = useState('')
  const [refundBusy, setRefundBusy] = useState(false)
  const [refundErr, setRefundErr] = useState<string | null>(null)
  const [refunded, setRefunded] = useState(false)
  const [refundPayments, setRefundPayments] = useState<BackendOrderPaymentDto[]>([])
  const [refundPaymentsLoading, setRefundPaymentsLoading] = useState(false)
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)
  /** Un pago se puede reembolsar si fue capturado (o reembolsado en parte). */
  const isRefundable = (p: BackendOrderPaymentDto) => p.status === 'CAPTURED' || p.status === 'PARTIALLY_REFUNDED'
  function openRefund() {
    if (!receipt) return
    setRefundReason(''); setRefundErr(null); setSelectedPaymentId(null); setRefundPayments([])
    setRefundOpen(true)
    // Cargar los pagos REALES del backend (traen status + refundedCents, que los
    // RegisteredPayment del receipt no tienen) para poblar el selector. Si la
    // llamada falla o no hay pagos, el selector no se muestra y submitRefund cae
    // al comportamiento de un solo pago (receipt.payments[0]).
    setRefundPaymentsLoading(true)
    void listOrderPayments(receipt.order.id)
      .then((fetched) => {
        setRefundPayments(fetched)
        const refundable = fetched.filter(isRefundable)
        // Preselección: si solo hay uno reembolsable, ya queda elegido.
        setSelectedPaymentId(refundable.length === 1 ? refundable[0]!.id : null)
      })
      .catch(() => { setRefundPayments([]) })
      .finally(() => setRefundPaymentsLoading(false))
  }
  async function submitRefund() {
    if (!receipt) return
    const reason = refundReason.trim()
    if (!reason) { setRefundErr('Indica el motivo del reembolso'); return }
    const refundable = refundPayments.filter(isRefundable)
    // Con 2+ pagos reembolsables el cajero DEBE elegir cuál.
    if (refundable.length > 1 && !selectedPaymentId) {
      setRefundErr('Elige el pago que vas a reembolsar')
      return
    }
    setRefundBusy(true); setRefundErr(null)
    try {
      const auth = await requireManagerAuth('ticket.refund')
      if (!auth.proceed) { setRefundBusy(false); return }
      // Pago a reembolsar: el elegido → el único reembolsable → primero del receipt → fetch.
      let paymentId = selectedPaymentId
        ?? (refundable.length === 1 ? refundable[0]!.id : undefined)
        ?? receipt.payments[0]?.id
      if (!paymentId) {
        const fetched = await listOrderPayments(receipt.order.id).catch(() => [])
        paymentId = fetched.find(isRefundable)?.id ?? fetched[0]?.id
      }
      if (!paymentId) throw new Error('No hay un pago que reembolsar en este ticket')
      await refundOrderPayment(receipt.order.id, paymentId, { reason, refundedById: currentUserId ?? 'unknown' })
      setRefundOpen(false)
      setRefunded(true)
      showToast('Reembolso registrado')
      void ticketQuery.refetch()
    } catch (e) {
      setRefundErr(e instanceof Error ? e.message : 'No se pudo reembolsar')
    } finally { setRefundBusy(false) }
  }

  // ── "Reembolsar orden" — order-level refund (F3, new endpoint) ──
  // Distinct from the payment-level flow above: this targets POST /v1/orders/:id/refund
  // and writes a PosRefund row + emits dhk8.pos.refund.created. The dialog handles its
  // own busy/error UI; we just listen for the result to refresh the ticket.
  const [orderRefundOpen, setOrderRefundOpen] = useState(false)

  // Lane #172 — Reabrir ticket: gerente revierte CLOSED → OPEN para corregir.
  // El servidor preserva pagos; el cajero decide refund o ajuste tras reabrir.
  const [reopenBusy, setReopenBusy] = useState(false)
  async function handleReopen() {
    if (!order) return
    if (order.status !== 'CLOSED') return
    const auth = await requireManagerAuth('ticket.reopen', {
      requireReason: true,
      contextLabel: `Ticket ${order.folio}${order.paidCents ? ' · pagado ' + formatCents(order.paidCents) : ''}`,
    })
    if (!auth.proceed) return
    const reason = auth.reason
    setReopenBusy(true)
    try {
      await reopenOrder(order.id, (reason ?? '').trim() || 'Reabierto por gerente', auth.managerAuthToken)
      // Redirige a /order para continuar editando.
      navigate(`/order?orderId=${order.id}`)
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e instanceof Error ? e.message : 'No se pudo reabrir el ticket')
    } finally {
      setReopenBusy(false)
    }
  }

  // ── Pre-cuenta (orden ABIERTA) — trabajarla desde su ficha, mismo set que el
  // listado de Tickets: Cobrar/Reanudar son navegaciones a /order; Cancelar anula
  // con PIN de gerente + motivo. Cierra el callejón sin salida de la ficha.
  const [precuentaBusy, setPrecuentaBusy] = useState(false)
  async function handleCancelPrecuenta() {
    if (!order || order.status === 'CLOSED' || order.status === 'CANCELLED') return
    const auth = await requireManagerAuth('order.voidOrder', {
      requireReason: true,
      contextLabel: `Pre-cuenta ${order.folio} — ${formatCents(order.totalCents)}`,
    })
    if (!auth.proceed) return
    setPrecuentaBusy(true)
    try {
      await cancelOrder(order.id, (auth.reason ?? '').trim() || 'Anulada por gerente', auth.managerAuthToken)
      showToast('Pre-cuenta cancelada')
      navigate('/tickets')
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e instanceof Error ? e.message : 'No se pudo cancelar la pre-cuenta')
    } finally {
      setPrecuentaBusy(false)
    }
  }

  // ── "Facturar este ticket" — BottomSheet (CFDI 4.0) ──
  const [facturarOpen, setFacturarOpen] = useState(false)
  const enqueueReceipt = useEnqueueReceipt()
  const enqueuePrecuenta = useEnqueuePrecuenta()
  const [issuedCfdi, setIssuedCfdi] = useState<ReceiptCfdi | null>(null)

  function handlePrint() {
    // Use the same thermal template + hidden iframe used by the cash-close flow,
    // so reprint output matches what was given to the customer originally.
    // Pass the FULL OrderDto (not just id) so the hook doesn't fall back to its
    // empty-placeholder branch when the order isn't loaded in useOrderStore.
    if (receipt) {
      const orderInput = receipt.order ?? receipt.id
      if (isPrecuenta) {
        void enqueuePrecuenta.mutateAsync(orderInput).catch(() => null)
      } else {
        void enqueueReceipt.mutateAsync(orderInput).catch(() => null)
      }
      return
    }
    // Fallback (no receipt loaded): keep the legacy fullscreen print path.
    document.body.classList.add('dhk8-printing')
    const cleanup = () => { document.body.classList.remove('dhk8-printing'); window.removeEventListener('afterprint', cleanup) }
    window.addEventListener('afterprint', cleanup)
    window.print()
  }

  // ── Branding del header del ticket ────────────────────────────────────────
  // Cascada: TenantBranding.displayName / logoUrl → fallback al brand de build
  // (NintaiGO si el branding no se ha cargado todavía). El subtítulo es siempre
  // el host del POS white-label (`pos.nintaigo.com`) — NO es por-tenant porque
  // el POS está montado bajo ese subdominio. El nombre nunca debe quedar como
  // "DocketHub POS" hardcoded — eso era el placeholder pre-branding.
  const branding = getBranding()
  const accountName = useAuthStore((s) => s.accountName)
  const businessName = accountName?.trim() || branding.name?.trim() || 'NintaiGO POS'
  const tenantLogoUrl = branding.logoUrl

  // ── "Atendió" — resolver userId → displayName ─────────────────────────────
  // El `waiterName` que viene del adapter es un UUID raw cuando la orden fue
  // cerrada por staff (closedByUserId). Cascada:
  //   1. Si el id coincide con el usuario logueado (caso típico: el mismo
  //      cajero abrió, cobró y ahora reimprime), usamos su fullName del store
  //      — evita un round-trip y funciona aún si /api/users/:id casca por RLS.
  //   2. Else, intentamos GET /api/users/:id del platform-auth.
  //   3. Fallback final: "Cajero".
  const currentUser = useAuthStore((s) => s.user)
  const rawWaiter = receipt?.waiterName ?? null
  const isSelf = !!rawWaiter && !!currentUser?.id && rawWaiter === currentUser.id
  const needsUserLookup = !!rawWaiter && looksLikeUuid(rawWaiter) && !isSelf
  const waiterLookup = useQuery({
    queryKey: ['user-brief', rawWaiter ?? 'none'],
    queryFn: ({ signal }) => getUserById(rawWaiter!, signal),
    enabled: needsUserLookup,
    staleTime: 5 * 60_000,
  })
  let waiterDisplayName: string | null
  if (isSelf && currentUser?.fullName) {
    waiterDisplayName = currentUser.fullName
  } else if (needsUserLookup) {
    waiterDisplayName = waiterLookup.data?.fullName ?? (waiterLookup.isFetched ? 'Cajero' : null)
  } else {
    waiterDisplayName = rawWaiter && !looksLikeUuid(rawWaiter) ? rawWaiter : null
  }

  // ── QR público del ticket ────────────────────────────────────────────────
  // URL: https://tickets.nintaigo.com/{publicTicketUuid}. El UUIDv7 lo genera
  // el backend al CLOSED (o lo backfillea on-read en getById para órdenes
  // legacy). Pintamos un SVG dataURL para que no consuma memoria por canvas.
  const publicUuid = receipt?.order.publicTicketUuid ?? null
  const ticketPublicUrl = publicUuid ? `https://${PUBLIC_TICKETS_HOST}/${publicUuid}` : null
  const [qrSvgDataUrl, setQrSvgDataUrl] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    if (!ticketPublicUrl) {
      setQrSvgDataUrl(null)
      return () => { alive = false }
    }
    QRCode.toString(ticketPublicUrl, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#0c1f12', light: '#ffffff' },
    })
      .then((svg) => {
        if (!alive) return
        const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
        setQrSvgDataUrl(dataUrl)
      })
      .catch(() => alive && setQrSvgDataUrl(null))
    return () => { alive = false }
  }, [ticketPublicUrl])

  if (ticketQuery.isLoading && !fallback) {
    return <div className="dhk8-receipt-empty"><p>Cargando ticket…</p></div>
  }

  if (!receipt) {
    return (
      <div className="dhk8-receipt-empty">
        <p>No hay un ticket para mostrar.</p>
        <button type="button" onClick={() => navigate('/tickets')}>Ver tickets</button>
      </div>
    )
  }

  const { order, payments } = receipt
  const cfdi = issuedCfdi ?? receipt.cfdi
  const cashGiven = receipt.cashGivenCents
  const change = receipt.changeCents

  return (
    <div className="dhk8-receipt-root">
      <div className="dhk8-receipt-toolbar">
        <button type="button" className="dhk8-receipt-btn" onClick={() => navigate('/tickets')}>
          <ArrowLeft size={14} /> Tickets
        </button>
        <div className="dhk8-receipt-actions">
          {isPrecuenta && order.status !== 'CANCELLED' && (
            <>
              <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--primary" onClick={() => navigate(`/order?orderId=${order.id}&pay=1`)}>
                <DollarSign size={14} /> Cobrar
              </button>
              <button type="button" className="dhk8-receipt-btn" onClick={() => navigate(`/order?orderId=${order.id}`)}>
                <Pencil size={14} /> Reanudar
              </button>
              <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--danger" onClick={() => void handleCancelPrecuenta()} disabled={precuentaBusy}>
                <Ban size={14} /> {precuentaBusy ? 'Cancelando…' : 'Cancelar'}
              </button>
            </>
          )}
          <button type="button" className={`dhk8-receipt-btn${isPrecuenta ? '' : ' dhk8-receipt-btn--primary'}`} onClick={handlePrint}>
            <Printer size={14} /> {isPrecuenta ? 'Imprimir pre-cuenta' : 'Reimprimir'}
          </button>
          <button type="button" className="dhk8-receipt-btn" onClick={openResend}>
            <Send size={14} /> Reenviar
          </button>
          {!isPrecuenta && !refunded && (order.status === 'CLOSED' || receipt.payments.length > 0) && (
            <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--danger" onClick={openRefund}>
              <RotateCcw size={14} /> Reembolsar
            </button>
          )}
          {!isPrecuenta && !refunded && order.status === 'CLOSED' && (order.paidCents ?? 0) > 0 && (
            <button
              type="button"
              className="dhk8-receipt-btn dhk8-receipt-btn--danger"
              onClick={() => setOrderRefundOpen(true)}
              title="Reembolsar la orden completa o parcial (registra un PosRefund)"
            >
              <RotateCcw size={14} /> Reembolsar orden
            </button>
          )}
          {/* Lane #172 — Reabrir ticket (gerente). Sólo CLOSED. */}
          {!isPrecuenta && order.status === 'CLOSED' && (
            <button
              type="button"
              className="dhk8-receipt-btn"
              onClick={() => void handleReopen()}
              disabled={reopenBusy}
              title="Reabrir el ticket (regresa a edición; los pagos se mantienen)"
            >
              <Unlock size={14} /> {reopenBusy ? 'Reabriendo…' : 'Reabrir'}
            </button>
          )}
        </div>
      </div>

      {isPrecuenta && (
        <div className="dhk8-receipt-precuenta-flag" role="note">
          <FileClock size={14} aria-hidden /> PRE-CUENTA · no fiscal — no es comprobante de pago.
        </div>
      )}

      {/* ── On-screen "Apple receipt" card ── */}
      <div className="dhk8-receipt-card" role="region" aria-label={isPrecuenta ? `Pre-cuenta ${order.folio}` : `Ticket ${order.folio}`}>
        {tenantLogoUrl ? (
          <img
            className="dhk8-receipt-card__logo"
            src={tenantLogoUrl}
            alt={businessName}
            aria-hidden="true"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="dhk8-receipt-card__glyph" aria-hidden="true">{isPrecuenta ? '◌' : '◇'}</div>
        )}
        <div className="dhk8-receipt-card__brand">{businessName}</div>
        <div className="dhk8-receipt-card__sub">{isPrecuenta ? 'PRE-CUENTA · no fiscal' : POS_PUBLIC_HOST}</div>

        <div className="dhk8-receipt-rule" />

        <dl className="dhk8-receipt-meta">
          {isPrecuenta ? (
            <>
              <div><dt>Cuenta</dt><dd>#{order.folio}</dd></div>
              <div><dt>Abierta</dt><dd>{formatDateMx(order.openedAt)}</dd></div>
            </>
          ) : (
            <>
              <div><dt>Folio</dt><dd>{order.folio}</dd></div>
              <div><dt>Fecha</dt><dd>{formatDateMx(receipt.closedAt)}</dd></div>
            </>
          )}
          {receipt.tableCode && <div><dt>Mesa</dt><dd>{receipt.tableCode}</dd></div>}
          <div><dt>Comensales</dt><dd>{order.covers}</dd></div>
          {waiterDisplayName && <div><dt>Atendió</dt><dd>{waiterDisplayName}</dd></div>}
        </dl>

        <div className="dhk8-receipt-rule" />

        <ul className="dhk8-receipt-items">
          {order.items.map((it) => {
            // 2026-05-25: desglosar modifiers con su priceDelta. Igual que
            // FastBarPage (Lane C) y BarraDisplayPage: precio base c/u en gris
            // + add-ons "+$X.XX" + total bold. baseUnitCents se deriva del
            // total/quantity menos suma de deltas (resilient: maneja datos
            // legacy donde unitPriceCents puede estar foldeado).
            const sumDeltas = it.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
            const baseUnitCents = Math.max(0, Math.round(it.totalCents / Math.max(1, it.quantity)) - sumDeltas)
            return (
              <li key={it.id} className="dhk8-receipt-item">
                <span className="dhk8-receipt-item__qty">{it.quantity}×</span>
                <span className="dhk8-receipt-item__name">
                  {it.name}
                  {it.modifiers.length > 0 && (
                    <ul className="dhk8-receipt-item__mods">
                      {it.modifiers.map((m, i) => (
                        <li key={i} className="dhk8-receipt-item__mod">
                          <span>+ {m.name}</span>
                          {m.priceDeltaCents !== 0 && (
                            <span className="dhk8-receipt-item__mod-delta">
                              {m.priceDeltaCents > 0 ? '+' : '−'}{formatCents(Math.abs(m.priceDeltaCents))}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {it.notes && <span className="dhk8-receipt-item__note">"{it.notes}"</span>}
                </span>
                <span className="dhk8-receipt-item__amount">
                  {sumDeltas > 0 && (
                    <span className="dhk8-receipt-item__unit">{formatCents(baseUnitCents)} c/u</span>
                  )}
                  <span className="dhk8-receipt-item__total">{formatCents(it.totalCents)}</span>
                </span>
              </li>
            )
          })}
        </ul>

        <div className="dhk8-receipt-rule" />

        <dl className="dhk8-receipt-totals">
          <div><dt>Subtotal</dt><dd>{formatCents(order.subtotalCents)}</dd></div>
          {order.discountCents > 0 && <div><dt>Descuento</dt><dd>−{formatCents(order.discountCents)}</dd></div>}
          <div><dt>IVA 16%</dt><dd>{formatCents(order.taxCents)}</dd></div>
          {receipt.tipCents > 0 && <div><dt>Propina</dt><dd>{formatCents(receipt.tipCents)}</dd></div>}
          <div className="dhk8-receipt-totals__grand"><dt>{isPrecuenta ? 'Total estimado' : 'Total'}</dt><dd>{formatCents(order.totalCents)}</dd></div>
        </dl>

        {!isPrecuenta && payments.length > 0 && (
          <>
            <div className="dhk8-receipt-rule" />
            <dl className="dhk8-receipt-totals">
              <div className="dhk8-receipt-totals__head"><dt>Pagos</dt><dd /></div>
              {payments.map((p) => (
                <div key={p.id}><dt>{HUMAN_METHOD[p.method] ?? p.method}{p.cardLastFour ? ` ····${p.cardLastFour}` : ''}</dt><dd>{formatCents(p.amountCents)}</dd></div>
              ))}
              {cashGiven !== null && cashGiven > 0 && <div><dt>Efectivo recibido</dt><dd>{formatCents(cashGiven)}</dd></div>}
              {change > 0 && <div className="dhk8-receipt-totals__grand"><dt>Cambio</dt><dd>{formatCents(change)}</dd></div>}
            </dl>
          </>
        )}

        {/* ── QR público del ticket (solo CLOSED + uuid disponible) ─────────── */}
        {!isPrecuenta && qrSvgDataUrl && ticketPublicUrl && (
          <>
            <div className="dhk8-receipt-rule" />
            <div className="dhk8-receipt-qr" role="region" aria-label="QR para descargar el ticket">
              <img className="dhk8-receipt-qr__img" src={qrSvgDataUrl} alt={`QR ${ticketPublicUrl}`} />
              <div className="dhk8-receipt-qr__caption">Escanea para descargar el ticket</div>
            </div>
          </>
        )}

        <div className="dhk8-receipt-rule" />
        <div className="dhk8-receipt-card__foot">
          {isPrecuenta ? 'Pre-cuenta para revisión del cliente — solicita la cuenta final para cerrar.' : '¡Gracias por su preferencia!'}
        </div>
      </div>

      {/* ── CFDI-lite section (only for emitted tickets) ── */}
      {!isPrecuenta && (
        <div className={`dhk8-receipt-cfdi${cfdi ? '' : ' dhk8-receipt-cfdi--none'}`} role="region" aria-label="Factura CFDI">
          {cfdi ? (
            <>
              <div className="dhk8-receipt-cfdi__top">
                <FileCheck2 size={16} className="dhk8-receipt-cfdi__icon" />
                <div className="dhk8-receipt-cfdi__title">Factura CFDI 4.0</div>
                <span className="dhk8-receipt-cfdi__badge">{cfdi.satStatus}</span>
              </div>
              <dl className="dhk8-receipt-cfdi__meta">
                <div><dt>UUID</dt><dd className="dhk8-receipt-cfdi__uuid">{cfdi.uuid}</dd></div>
                <div><dt>Timbrado</dt><dd>{formatDateMx(cfdi.stampedAt)}</dd></div>
                <div><dt>RFC receptor</dt><dd>{cfdi.rfcReceptor}</dd></div>
                <div><dt>Receptor</dt><dd>{cfdi.razonSocialReceptor}</dd></div>
                <div><dt>Uso CFDI</dt><dd>{cfdi.usoCfdi}</dd></div>
                <div><dt>Total facturado</dt><dd>{formatCents(cfdi.totalCents)}</dd></div>
              </dl>
              <div className="dhk8-receipt-cfdi__actions">
                <a className="dhk8-receipt-btn" href={cfdi.xmlUrl} target="_blank" rel="noreferrer"><FileText size={14} /> Ver XML</a>
                <a className="dhk8-receipt-btn" href={cfdi.pdfUrl} target="_blank" rel="noreferrer"><FileText size={14} /> Ver PDF</a>
              </div>
            </>
          ) : (
            <>
              <div className="dhk8-receipt-cfdi__top">
                <FileText size={16} className="dhk8-receipt-cfdi__icon" />
                <div className="dhk8-receipt-cfdi__title">Sin factura</div>
              </div>
              <p className="dhk8-receipt-cfdi__hint">Este ticket aún no tiene CFDI. El cliente puede solicitarlo aquí. La emisión va por la cadena del proveedor fiscal: DocketHub → Omnifiscal → FiscalAPI → SAT.</p>
              <div className="dhk8-receipt-cfdi__actions">
                <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--primary" onClick={() => setFacturarOpen(true)}>
                  <FileText size={14} /> Facturar este ticket
                </button>
                <button type="button" className="dhk8-receipt-btn" onClick={() => navigate(`/billing?orderId=${order.id}`)}>
                  Ir a Facturas
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Lane #175.B.1 — Timeline de intents de facturación (renderiza solo
          si hubo actividad: QR escaneado, datos capturados, stamp success/error).
          Visible al staff para diagnosticar problemas que el cliente reporta. */}
      <InvoiceAttemptsTimeline orderId={order.id} />

      {/* ── Hidden thermal print root (Reimprimir / Imprimir pre-cuenta) ── */}
      <div className="dhk8-receipt-printroot dhk8-ticket-print-root" aria-hidden="true">
        <TicketPreview
          order={order}
          payments={payments}
          tipCents={receipt.tipCents}
          cashGivenCents={cashGiven ?? undefined}
          waiterName={receipt.waiterName}
          tableCode={receipt.tableCode}
          hasInvoice={!!cfdi}
        />
      </div>

      {/* ── "Reenviar" modal ── */}
      {resendOpen && (
        <div className="dhk8-resend-overlay" role="dialog" aria-modal="true" aria-label="Reenviar ticket" onClick={() => !resendBusy && setResendOpen(false)}>
          <div className="dhk8-resend" onClick={(e) => e.stopPropagation()}>
            <div className="dhk8-resend__title">{isPrecuenta ? 'Enviar pre-cuenta' : 'Reenviar ticket'}</div>
            <p className="dhk8-resend__hint">Indica un correo o un número de WhatsApp. El envío lo realiza el servidor.</p>
            <label className="dhk8-resend__field">
              <span>Correo</span>
              <input type="email" inputMode="email" value={resendEmail} onChange={(e) => setResendEmail(e.currentTarget.value)} placeholder="cliente@correo.com" />
            </label>
            <label className="dhk8-resend__field">
              <span>WhatsApp</span>
              <input type="tel" inputMode="tel" value={resendWa} onChange={(e) => setResendWa(e.currentTarget.value)} placeholder="+52 55 1234 5678" />
            </label>
            {resendErr && <div className="dhk8-resend__err">{resendErr}</div>}
            <div className="dhk8-resend__actions">
              <button type="button" className="dhk8-receipt-btn" disabled={resendBusy} onClick={() => setResendOpen(false)}>Cancelar</button>
              <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--primary" disabled={resendBusy} onClick={() => void submitResend()}>
                {resendBusy ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── "Reembolsar" modal — confirm + motivo, PIN-gated (ticket.refund) ── */}
      {refundOpen && (
        <div className="dhk8-resend-overlay" role="dialog" aria-modal="true" aria-label="Reembolsar ticket" onClick={() => !refundBusy && setRefundOpen(false)}>
          <div className="dhk8-resend" onClick={(e) => e.stopPropagation()}>
            <div className="dhk8-resend__title">Reembolsar ticket {order.folio}</div>
            {(() => {
              const refundable = refundPayments.filter(isRefundable)
              const multi = refundable.length > 1
              return (
                <>
                  <p className="dhk8-resend__hint">
                    {multi
                      ? 'Este ticket tiene varios pagos. Elige cuál reembolsar. Requiere autorización de gerente.'
                      : `Se revertirá el pago de ${formatCents(order.totalCents)}. Requiere autorización de gerente. Indica el motivo.`}
                  </p>
                  {/* Selector de pago — solo cuando hay 2+ pagos reembolsables. */}
                  {multi && (
                    <div className="dhk8-resend__field" role="radiogroup" aria-label="Pago a reembolsar">
                      <span>Pago a reembolsar</span>
                      <div className="dhk8-refund-paylist">
                        {refundable.map((p) => {
                          const selected = selectedPaymentId === p.id
                          const refundableCents = Math.max(0, p.amountCents - p.refundedCents)
                          return (
                            <button
                              key={p.id}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              className={`dhk8-refund-payopt${selected ? ' dhk8-refund-payopt--selected' : ''}`}
                              onClick={() => setSelectedPaymentId(p.id)}
                            >
                              <span className="dhk8-refund-payopt__method">
                                {HUMAN_METHOD[p.method] ?? p.method}{p.cardLastFour ? ` ····${p.cardLastFour}` : ''}
                                {p.status === 'PARTIALLY_REFUNDED' && <span className="dhk8-refund-payopt__tag"> · parcial</span>}
                              </span>
                              <span className="dhk8-refund-payopt__amt">{formatCents(refundableCents)}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {refundPaymentsLoading && <p className="dhk8-resend__hint">Cargando pagos…</p>}
                </>
              )
            })()}
            <label className="dhk8-resend__field">
              <span>Motivo</span>
              <input type="text" value={refundReason} onChange={(e) => setRefundReason(e.currentTarget.value)} placeholder="p. ej. cobro duplicado, error de captura" autoFocus />
            </label>
            {refundErr && <div className="dhk8-resend__err">{refundErr}</div>}
            <div className="dhk8-resend__actions">
              <button type="button" className="dhk8-receipt-btn" disabled={refundBusy} onClick={() => setRefundOpen(false)}>Cancelar</button>
              <button type="button" className="dhk8-receipt-btn dhk8-receipt-btn--danger" disabled={refundBusy} onClick={() => void submitRefund()}>
                {refundBusy ? 'Procesando…' : 'Reembolsar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── "Facturar este ticket" — BottomSheet (CFDI 4.0) ── */}
      <FacturarSheet
        open={facturarOpen}
        onClose={() => setFacturarOpen(false)}
        orderId={order.id}
        totalCents={order.totalCents}
        ticketFolio={order.folio}
        concept={`Consumo en restaurante · ticket ${order.folio}`}
        onIssued={(inv) => {
          setIssuedCfdi({
            uuid: inv.cfdiUuid ?? '',
            serie: inv.serie,
            folio: inv.folio,
            stampedAt: inv.cfdiStampedAt ?? new Date().toISOString(),
            satStatus: 'TIMBRADA',
            rfcReceptor: inv.customer?.rfc ?? '',
            razonSocialReceptor: inv.customer?.legalName ?? '',
            usoCfdi: labelForUso(inv.cfdiUsage),
            totalCents: inv.totalCents,
            xmlUrl: '#',
            pdfUrl: '#',
          })
          showToast(`Factura ${inv.serie}-${inv.folio} timbrada`)
        }}
      />

      {/* ── "Reembolsar orden" — F3 order-level refund (PosRefund + event) ── */}
      <RefundDialog
        open={orderRefundOpen}
        order={order}
        currentUserId={currentUserId ?? 'unknown'}
        onClose={() => setOrderRefundOpen(false)}
        onRefunded={() => {
          setRefunded(true)
          showToast('Reembolso de orden registrado')
          void ticketQuery.refetch()
        }}
      />

      {toast && <div className="dhk8-receipt-toast" role="status">{toast}</div>}

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-receipt-root { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); min-height:0; flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:var(--pos-space-4,16px); }
  .dhk8-receipt-toolbar { display:flex; justify-content:space-between; align-items:center; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-receipt-actions { display:flex; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-receipt-btn { display:inline-flex; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-4,16px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); cursor:pointer; text-decoration:none; white-space:nowrap; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-receipt-btn:hover { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-receipt-btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-receipt-btn--primary { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-receipt-btn--primary:hover { background:var(--pos-tint,#1F8B3A); filter:brightness(1.06); color:#fff; }
  .dhk8-receipt-btn--danger { background:transparent; color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 45%, transparent); }
  .dhk8-receipt-btn--danger:hover { background:var(--pos-danger-bg,rgba(229,72,77,.14)); color:var(--pos-danger,#E5484D); }

  .dhk8-receipt-precuenta-flag { align-self:center; width:100%; max-width:380px; display:flex; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-radius:var(--pos-radius-md,8px); background:var(--pos-warning-bg,rgba(241,179,59,.18)); color:var(--pos-warning,#B8780B); border:1px solid color-mix(in oklch, var(--pos-warning,#B8780B) 30%, transparent); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); letter-spacing:.02em; }

  .dhk8-receipt-card { align-self:center; width:100%; max-width:380px; background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-xl,16px); box-shadow:var(--pos-shadow-2,0 8px 24px -8px rgba(0,0,0,.4)); padding:var(--pos-space-5,20px) var(--pos-space-5,20px) var(--pos-space-4,16px); display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-receipt-card__glyph { align-self:center; width:36px; height:36px; display:flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-pill,999px); background:var(--pos-tint-soft,rgba(31,139,58,.12)); color:var(--pos-tint,#1F8B3A); font-size:1.1rem; }
  .dhk8-receipt-card__logo { align-self:center; max-width:88px; max-height:48px; width:auto; height:auto; object-fit:contain; border-radius:var(--pos-radius-sm,6px); background:#fff; padding:4px 6px; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-receipt-card__brand { text-align:center; font-family:var(--font-display,system-ui); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-receipt-card__sub { text-align:center; font-size:var(--pos-text-xxs,11px); letter-spacing:.04em; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-receipt-card__foot { text-align:center; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }

  .dhk8-receipt-qr { display:flex; flex-direction:column; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-3,12px) 0 var(--pos-space-1,4px); }
  .dhk8-receipt-qr__img { width:144px; height:144px; background:#fff; padding:8px; border-radius:var(--pos-radius-md,8px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-receipt-qr__caption { font-size:var(--pos-text-xxs,11px); letter-spacing:.02em; color:var(--pos-fg-tertiary,#7E8C82); text-align:center; }
  .dhk8-receipt-rule { height:1px; background:var(--pos-separator,rgba(255,255,255,.07)); margin:var(--pos-space-1,4px) 0; }

  .dhk8-receipt-meta, .dhk8-receipt-totals { margin:0; display:flex; flex-direction:column; gap:3px; }
  .dhk8-receipt-meta > div, .dhk8-receipt-totals > div { display:flex; justify-content:space-between; gap:var(--pos-space-3,12px); font-size:var(--pos-text-xs,12px); }
  .dhk8-receipt-meta dt, .dhk8-receipt-totals dt { margin:0; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-receipt-meta dd, .dhk8-receipt-totals dd { margin:0; font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; color:var(--pos-fg-secondary,#B6BFB8); text-align:right; }
  .dhk8-receipt-totals__head dt { font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; }
  .dhk8-receipt-totals__grand { padding-top:4px; border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); margin-top:2px; }
  .dhk8-receipt-totals__grand dt { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-semibold,600); }
  .dhk8-receipt-totals__grand dd { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-semibold,600); font-size:var(--pos-text-sm,13px); }

  .dhk8-receipt-items { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-receipt-item { display:grid; grid-template-columns:28px 1fr auto; gap:var(--pos-space-2,8px); font-size:var(--pos-text-xs,12px); }
  .dhk8-receipt-item__qty { font-family:var(--font-mono,ui-monospace,monospace); text-align:right; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-receipt-item__name { display:flex; flex-direction:column; gap:2px; color:var(--pos-fg,#E8EFE9); line-height:1.25; min-width:0; }
  .dhk8-receipt-item__mods { list-style:none; margin:2px 0 0; padding:0; display:flex; flex-direction:column; gap:1px; }
  .dhk8-receipt-item__mod { display:flex; justify-content:space-between; gap:8px; font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-family:var(--font-mono,ui-monospace,monospace); }
  .dhk8-receipt-item__mod-delta { font-variant-numeric:tabular-nums; }
  .dhk8-receipt-item__note { font-size:var(--pos-text-xxs,11px); font-style:italic; color:var(--pos-fg-tertiary,#7E8C82); margin-top:2px; }
  .dhk8-receipt-item__amount { display:flex; flex-direction:column; align-items:flex-end; gap:1px; }
  .dhk8-receipt-item__unit { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-variant-numeric:tabular-nums; }
  .dhk8-receipt-item__total { font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); font-weight:600; }

  .dhk8-receipt-cfdi { align-self:center; width:100%; max-width:380px; background:var(--pos-elevated,#1E2520); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg,12px); padding:var(--pos-space-4,16px); display:flex; flex-direction:column; gap:var(--pos-space-3,12px); }
  .dhk8-receipt-cfdi--none { background:var(--pos-surface,#161C18); }
  .dhk8-receipt-cfdi__top { display:flex; align-items:center; gap:var(--pos-space-2,8px); }
  .dhk8-receipt-cfdi__icon { color:var(--pos-tint,#1F8B3A); flex-shrink:0; }
  .dhk8-receipt-cfdi--none .dhk8-receipt-cfdi__icon { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-receipt-cfdi__title { font-family:var(--font-display,system-ui); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); flex:1; }
  .dhk8-receipt-cfdi__badge { font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; padding:2px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px); background:var(--pos-success-bg,rgba(31,139,58,.16)); color:var(--pos-success,#1F8B3A); }
  .dhk8-receipt-cfdi__meta { margin:0; display:flex; flex-direction:column; gap:4px; }
  .dhk8-receipt-cfdi__meta > div { display:flex; justify-content:space-between; gap:var(--pos-space-3,12px); font-size:var(--pos-text-xs,12px); }
  .dhk8-receipt-cfdi__meta dt { margin:0; color:var(--pos-fg-tertiary,#7E8C82); flex-shrink:0; }
  .dhk8-receipt-cfdi__meta dd { margin:0; font-family:var(--font-mono,ui-monospace,monospace); color:var(--pos-fg-secondary,#B6BFB8); text-align:right; word-break:break-all; }
  .dhk8-receipt-cfdi__uuid { font-size:var(--pos-text-xxs,11px); }
  .dhk8-receipt-cfdi__hint { margin:0; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); line-height:1.4; }
  .dhk8-receipt-cfdi__actions { display:flex; gap:var(--pos-space-2,8px); flex-wrap:wrap; }

  .dhk8-receipt-empty { display:flex; flex-direction:column; align-items:center; gap:var(--pos-space-3,12px); padding:var(--pos-space-8,32px); font-size:var(--pos-text-md,15px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-receipt-empty button { padding:var(--pos-space-3,12px) var(--pos-space-4,16px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg,#E8EFE9); font:inherit; cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-receipt-empty button:hover { background:var(--pos-elevated,#1E2520); }

  .dhk8-resend-overlay { position:fixed; inset:0; z-index:var(--pos-z-sheet, 200); display:flex; align-items:center; justify-content:center; padding:var(--pos-space-4,16px); background:rgba(0,0,0,.5); }
  .dhk8-resend { width:100%; max-width:340px; display:flex; flex-direction:column; gap:var(--pos-space-3,12px); padding:var(--pos-space-5,20px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-xl,16px); box-shadow:var(--pos-shadow-3,0 16px 48px -12px rgba(0,0,0,.5)); }
  .dhk8-resend__title { font-family:var(--font-display,system-ui); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-resend__hint { margin:0; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); line-height:1.4; }
  .dhk8-resend__field { display:flex; flex-direction:column; gap:4px; font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-resend__field input { font:inherit; font-size:var(--pos-text-md,15px); text-transform:none; letter-spacing:normal; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-resend__field input:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-resend__err { color:var(--pos-danger,#E5484D); background:var(--pos-danger-bg,rgba(229,72,77,.14)); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }

  /* Selector de pago a reembolsar (2+ pagos). Lista de opciones tipo radio. */
  .dhk8-refund-paylist { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); text-transform:none; letter-spacing:normal; }
  .dhk8-refund-payopt { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-3,12px); width:100%; min-height:var(--pos-touch-target,44px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; text-align:left; cursor:pointer; transition:border-color var(--pos-duration-fast,120ms) ease, background var(--pos-duration-fast,120ms) ease; }
  .dhk8-refund-payopt:hover { border-color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-refund-payopt--selected { border-color:var(--pos-tint,#1F8B3A); background:var(--pos-tint-soft,rgba(31,139,58,.12)); box-shadow:0 0 0 1px var(--pos-tint,#1F8B3A) inset; }
  .dhk8-refund-payopt__method { font-size:var(--pos-text-sm,13px); }
  .dhk8-refund-payopt__tag { color:var(--pos-fg-tertiary,#7E8C82); font-size:var(--pos-text-xs,12px); }
  .dhk8-refund-payopt__amt { font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; font-weight:var(--pos-weight-semibold,600); }
  .dhk8-resend__actions { display:flex; gap:var(--pos-space-2,8px); justify-content:flex-end; }

  .dhk8-receipt-toast { position:fixed; bottom:var(--pos-space-6,24px); left:50%; transform:translateX(-50%); z-index:var(--pos-z-toast, 300); padding:var(--pos-space-3,12px) var(--pos-space-5,20px); font-size:var(--pos-text-sm,13px); border:1px solid color-mix(in oklch, var(--pos-tint,#1F8B3A) 50%, transparent); background:var(--pos-elevated,#1E2520); color:var(--pos-success,#1F8B3A); border-radius:var(--pos-radius-pill,999px); box-shadow:var(--pos-shadow-3,0 16px 48px -12px rgba(0,0,0,.5)); }

  .dhk8-receipt-printroot { position:absolute; left:-9999px; top:0; }
  @media print { .dhk8-receipt-printroot { position:static; left:0; } }
`
